import { FC, ReactElement } from 'react';

import {
  Card,
  Divider,
  Modal,
  ModalProps,
  styled,
  Typography,
  Button,
  Box,
} from '@mui/material';
import clsx from 'clsx';

import { IcCheckMark } from '@/assets/images';

const Root = styled(Modal)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'auto',

  '& .modal-content': {
    minWidth: 600,
    padding: 20,
    outline: 'none',
    borderRadius: 24,
    textAlign: 'center',
    [theme.breakpoints.up('lg')]: {
      maxWidth: 1000,
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 1400,
    },
    '& .header': {
      display: 'flex',
      justifyContent: 'center',
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(2),
    },
    '& .divider': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    '& .icon-container': {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: theme.spacing(4),
    },
    '& .message': {
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(2),
      padding: theme.spacing(0, 2),
    },
    '& .close-button': {
      backgroundColor: '#E75757',
      color: 'white',
      padding: theme.spacing(1.5),
      fontSize: '1.2rem',
      marginTop: theme.spacing(2),
      '&:hover': {
        backgroundColor: '#D32F2F',
      },
    },
  },
  '& .fixed-width': {
    maxWidth: '600px',
  },
}));

export interface CFSModalProps extends Omit<ModalProps, 'children'> {
  open: boolean;
  hideCloseButton?: boolean;
  title?: string;
  message?: string;
  buttonText?: string;
  identifier?: string;
  classes?: {
    root?: string;
    content?: string;
    title?: string;
    divider?: string;
    iconContainer?: string;
    message?: string;
    button?: string;
  };
  children?: ReactElement;
  onClose: () => void;
}

export const FirstResponderAssignmentEndedModal: FC<CFSModalProps> = ({
  title,
  message,
  buttonText = 'Close',
  classes,
  onClose,
  ...rest
}) => {
  return (
    <Root onClose={onClose} className={classes?.root} {...rest}>
      <Card className={clsx('modal-content', 'fixed-width', classes?.content)}>
        <Typography
          variant="h4"
          component="h2"
          fontWeight={600}
          className={clsx('header', classes?.title)}
          sx={{ fontSize: '21px' }}
        >
          {title}
        </Typography>

        <Divider className={clsx('divider', classes?.divider)} />

        <Box
          className={clsx('icon-container', classes?.iconContainer)}
          sx={{ gap: 10 }}
        >
          <IcCheckMark />
        </Box>

        <Typography
          sx={{ fontSize: '21px', fontWeight: 600 }}
          className={clsx('message', classes?.message)}
        >
          {message}
        </Typography>

        <Divider className={clsx('divider', classes?.divider)} />

        <Button
          variant="contained"
          onClick={onClose}
          fullWidth
          className={clsx('close-button', classes?.button)}
        >
          {buttonText}
        </Button>
      </Card>
    </Root>
  );
};

export default FirstResponderAssignmentEndedModal;
